// global-data.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Servicio para almacenar y gestionar datos globales de la aplicación,
 * incluyendo cachés, configuraciones y datos compartidos entre componentes.
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalDataService {
  /** Clave para almacenar el cache de países filtrados para eSIM en localStorage */
  private CACHE_KEY_ESIM = 'filteredCountriesCache';

  /** Tiempo de expiración del cache del catálogo eSIM (1 hora en milisegundos) */
  readonly CACHE_EXPIRATION_MS = 1000 * 60 * 60;

  /** Tiempo de expiración del cache de eSIMs del usuario en memoria (16 minutos) */
  private USER_ESIMS_TTL = 1000 * 60 * 16;

  private _sendMoneyData: any;
  private _transfersUrlData: any;
  private _creditsUrlData: any;
  private _esimFilteredCountries: { [key: string]: any } = {};
  private _allow_free_transfer: boolean = false;

  /** eSIMs del usuario cacheadas en memoria */
  private _userEsims: any[] | null = null;
  private _userEsimsTimestamp: number = 0;
  private _userEsimsLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly userEsimsLoading$ = this._userEsimsLoadingSubject.asObservable();

  /** Número total de eSIMs del usuario. -1 indica desconocido (no cargado todavía). */
  userEsimsCount: number = -1;

  /** 
   * Datos relacionados con el envío de dinero, puede contener configuración o estado.
   */
  set sendMoneyData(value: any) {
    this._sendMoneyData = value;
  }
  get sendMoneyData(): any {
    return this._sendMoneyData;
  }

  /**
   * Países filtrados para la funcionalidad eSIM.
   */
  get esimFilteredCountries(): any {
    return this._esimFilteredCountries;
  }
  set esimFilteredCountries(value: any) {
    this._esimFilteredCountries = value;
  }

  /**
   * Indica si un objeto tiene propiedades (no está vacío).
   * @param obj Objeto a evaluar.
   * @returns `true` si el objeto no está vacío, `false` en caso contrario.
   */
  isNotEmptyObject(obj: any): boolean {
    return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
  }

  /**
   * Obtiene los países filtrados para eSIM de un tipo específico.
   * Intenta cargar desde cache localStorage si no están en memoria y si la cache es válida.
   * 
   * @param tipo Tipo de filtro para obtener países específicos.
   * @returns Los países filtrados para ese tipo o `undefined` si no hay datos.
   */
  public getEsimFilteredCountries(tipo: string) {
    if (!this.isNotEmptyObject(this._esimFilteredCountries)) {
      const now = Date.now();
      const cachedEsim = localStorage.getItem(this.CACHE_KEY_ESIM);
      if (cachedEsim) {
        try {
          const { data, timestamp } = JSON.parse(cachedEsim);
          if (now - timestamp < this.CACHE_EXPIRATION_MS) {
            this._esimFilteredCountries = data;
          }
        } catch (e) {
          console.warn('Error leyendo cache ESIM', e);
        }
      }
    }
    return this._esimFilteredCountries[tipo];
  }

  /**
   * Devuelve la lista de eSIMs del usuario desde caché en memoria si es válida.
   * @returns Array de eSIMs o null si el caché ha expirado o no existe.
   */
  getUserEsims(): any[] | null {
    if (!this._userEsims) return null;
    if (Date.now() - this._userEsimsTimestamp > this.USER_ESIMS_TTL) {
      this._userEsims = null;
      return null;
    }
    return this._userEsims;
  }

  /**
   * Almacena la lista de eSIMs del usuario en caché en memoria y señaliza fin de carga.
   */
  setUserEsims(esims: any[]): void {
    this._userEsims = esims;
    this._userEsimsTimestamp = Date.now();
    this.userEsimsCount = esims.length;
    this._userEsimsLoadingSubject.next(false);
  }

  /**
   * Invalida el caché en memoria de las eSIMs del usuario.
   * Debe llamarse tras una compra o topup exitoso, y al hacer logout.
   */
  invalidateUserEsims(): void {
    this._userEsims = null;
    this._userEsimsTimestamp = 0;
    this.userEsimsCount = -1;
    this._userEsimsLoadingSubject.next(false);
  }

  /**
   * Señaliza que hay una carga de eSIMs del usuario en progreso.
   * Debe llamarse antes de lanzar la petición en background.
   */
  setUserEsimsLoading(): void {
    this._userEsimsLoadingSubject.next(true);
  }

  /**
   * Devuelve si hay una carga de eSIMs del usuario en progreso.
   */
  isUserEsimsLoading(): boolean {
    return this._userEsimsLoadingSubject.getValue();
  }

  /**
   * Devuelve true si el caché de eSIMs del usuario no existe o ha expirado,
   * sin modificar el estado del caché.
   */
  isUserEsimsStale(): boolean {
    if (!this._userEsims) return true;
    return Date.now() - this._userEsimsTimestamp > this.USER_ESIMS_TTL;
  }

  /**
   * Indica si se permite transferencias gratuitas.
   * Se almacena en sessionStorage de forma codificada para persistencia entre recargas.
   */
  set allowFreeTransfer(value: boolean) {
    sessionStorage.setItem(btoa('allow_free_transfer'), btoa(JSON.stringify(value ?? false)));
    this._allow_free_transfer = value;
  }
  get allowFreeTransfer() {
    const raw = sessionStorage.getItem(btoa('allow_free_transfer'));
    if (raw) { this._allow_free_transfer = JSON.parse(atob(raw)); }
    return this._allow_free_transfer;
  }

  /** Datos relacionados con la URL de transferencias. */
  set transfersUrlData(value: any) {
    this._transfersUrlData = value;
  }
  get transfersUrlData(): any {
    return this._transfersUrlData;
  }

  /** Datos relacionados con la URL de créditos. */
  set creditsUrlData(value: any) {
    this._creditsUrlData = value;
  }
  get creditsUrlData(): any {
    return this._creditsUrlData;
  }
}
